/**
 * Contratos da Fase 6: transferências entre depósitos, carga do vendedor (carregamentos),
 * vendas da rota e acerto. O banco repete as regras essenciais (ver migration
 * 0009_cargas_transferencias).
 */
import { z } from "zod";
import { decimalSchema, versionSchema } from "./cadastros";
import { reasonSchema, type StockMovement } from "./estoque";
import type { SalesStatus } from "./vendas";
import { fromScaled, toScaled } from "./quantities";

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional()
    .transform((v) => v ?? null);
const optionalUuid = z
  .union([z.uuid(), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v ? v : null));
const positiveQty = decimalSchema(6).refine(
  (v) => Number(v) > 0,
  "A quantidade precisa ser maior que zero",
);
const qtyOrZero = z
  .union([decimalSchema(6), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v === "" || v === null || v === undefined ? "0" : v));
const moneyOrZero = z
  .union([decimalSchema(2, { max: 999_999_999_999 }), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v === "" || v === null || v === undefined ? "0" : v));

// ---------------------------------------------------------------- itens (produto + quantidade)
export const stockDocItemSchema = z.object({
  productId: z.uuid("Selecione o produto"),
  unitId: z.uuid("Selecione a unidade"),
  quantity: positiveQty,
  notes: optionalText(300),
});
export type StockDocItemInput = z.input<typeof stockDocItemSchema>;

// ---------------------------------------------------------------- transferência
export const stockTransferInputSchema = z
  .object({
    originWarehouseId: z.uuid("Selecione o depósito de origem"),
    destinationWarehouseId: z.uuid("Selecione o depósito de destino"),
    reason: reasonSchema,
    items: z.array(stockDocItemSchema).min(1, "Inclua ao menos um item").max(200),
  })
  .refine((v) => v.originWarehouseId !== v.destinationWarehouseId, {
    message: "Origem e destino precisam ser depósitos diferentes",
    path: ["destinationWarehouseId"],
  });
export type StockTransferInput = z.input<typeof stockTransferInputSchema>;

export type StockTransfer = {
  id: string;
  number: string;
  status: "rascunho" | "confirmado" | "cancelado" | "estornado";
  originWarehouseId: string;
  originWarehouseName: string;
  destinationWarehouseId: string;
  destinationWarehouseName: string;
  reason: string;
  itemCount: number;
  createdAt: string;
  createdByEmail: string | null;
  confirmedAt: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  items?: {
    id: string;
    productId: string;
    productCode: string;
    productName: string;
    unitCode: string;
    quantity: string;
    notes: string | null;
  }[];
  movements?: StockMovement[];
};

// ---------------------------------------------------------------- carga
export const LOAD_STATUSES = ["aberta", "acertada", "cancelada"] as const;
export type LoadStatus = (typeof LOAD_STATUSES)[number];
export const LOAD_STATUS_LABEL: Record<LoadStatus, string> = {
  aberta: "Aberta (em rota)",
  acertada: "Acertada",
  cancelada: "Cancelada",
};

export const loadCreateSchema = z.object({
  salespersonId: z.uuid("Selecione o vendedor"),
  /** vazio: depósito padrão (fábrica) */
  originWarehouseId: optionalUuid,
  notes: optionalText(1000),
});
export type LoadCreateInput = z.input<typeof loadCreateSchema>;

export const loadCancelSchema = z.object({ ...versionSchema.shape, reason: reasonSchema });

export const loadQuerySchema = z.object({
  status: z.enum([...LOAD_STATUSES, "todas"]).default("todas"),
  salespersonId: z.uuid().optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const shipmentInputSchema = z.object({
  notes: optionalText(500),
  items: z.array(stockDocItemSchema).min(1, "Inclua ao menos um item").max(200),
});
export type ShipmentInput = z.input<typeof shipmentInputSchema>;

export type Load = {
  id: string;
  number: string;
  status: LoadStatus;
  salespersonId: string;
  salespersonName: string;
  warehouseId: string;
  warehouseName: string;
  originWarehouseId: string;
  originWarehouseName: string;
  notes: string | null;
  openedAt: string;
  settledAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdByEmail: string | null;
  version: number;
  shipmentCount: number;
  orderCount: number;
  /** total das vendas confirmadas da carga */
  salesTotal: string;
  /** o usuário logado é o vendedor desta carga (registra, mas não fecha nem confere PIX/cartão) */
  isOwn: boolean;
  /**
   * o usuário logado pode declarar e enviar a prestação de contas: o próprio vendedor, ou o
   * escritório em nome dele quando o vendedor não tem usuário ativo
   */
  canDeclare: boolean;
  /**
   * quem é o vendedor da carga: registrado na abertura ("confiavel") ou reconstituído para carga
   * anterior ao acerto simplificado; "historico_incerto" exige pessoas diferentes para confirmar a
   * conferência e finalizar o acerto
   */
  salespersonLinkStatus: SalespersonLinkStatus;
  /** situação do acerto em palavras (lista de cargas) */
  settlementSummary: LoadSettlementSummary;
};

export const LOAD_SETTLEMENT_SUMMARY_LABEL = {
  nao_iniciado: "Não iniciado",
  prestacao_enviada: "Prestação enviada",
  retorno_informado: "Retorno informado",
  conferido: "Conferido",
  finalizado: "Finalizado",
  prestacao_desatualizada: "Prestação desatualizada",
} as const;
export type LoadSettlementSummary = keyof typeof LOAD_SETTLEMENT_SUMMARY_LABEL;

export type SalespersonLinkStatus = "confiavel" | "historico_confirmado" | "historico_incerto";

export type LoadShipment = {
  id: string;
  sequence: number;
  status: "rascunho" | "confirmado" | "cancelado" | "estornado";
  notes: string | null;
  confirmedAt: string | null;
  confirmedByEmail: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  items: {
    id: string;
    productId: string;
    productCode: string;
    productName: string;
    unitCode: string;
    quantity: string;
  }[];
  /** lotes que saíram da origem e entraram no depósito do vendedor */
  lots: {
    productCode: string;
    lotCode: string | null;
    lotExpiresOn: string | null;
    quantity: string;
  }[];
};

export type LoadOrder = {
  id: string;
  number: string;
  status: SalesStatus;
  customerId: string;
  customerName: string;
  totalAmount: string;
  paymentMode: "titulo" | "a_vista" | null;
  paymentMethodName: string | null;
  receivableNumber: string | null;
  confirmedAt: string | null;
};

// ---------------------------------------------------------------- acerto
export const SETTLEMENT_STATUS_LABEL = {
  rascunho: "Em andamento",
  confirmado: "Finalizado",
  estornado: "Estornado",
} as const;

/** Origem do acerto: prestação de contas do vendedor (app/ERP) ou acerto feito pelo escritório. */
export const SETTLEMENT_ORIGIN_LABEL = {
  vendedor: "Prestação de contas do vendedor",
  escritorio: "Acerto pelo escritório",
} as const;
export type SettlementOrigin = keyof typeof SETTLEMENT_ORIGIN_LABEL;

/**
 * Etapa do acerto (calculada pela API; a tela só desenha):
 *  - retorno: informar o que voltou (mercadoria por lote e dinheiro por forma);
 *  - conferencia: tudo informado, falta confirmar (ou a confirmação ficou desatualizada);
 *  - finalizar: conferência confirmada, pronta para finalizar;
 *  - finalizado / cancelada: nada a fazer.
 */
export const SETTLEMENT_STAGES = [
  "retorno",
  "conferencia",
  "finalizar",
  "finalizado",
  "cancelada",
] as const;
export type SettlementStage = (typeof SETTLEMENT_STAGES)[number];
export const SETTLEMENT_STAGE_LABEL: Record<SettlementStage, string> = {
  retorno: "Informar o retorno",
  conferencia: "Confirmar a conferência",
  finalizar: "Finalizar",
  finalizado: "Finalizado",
  cancelada: "Carga cancelada",
};

/** Como o valor conferido foi informado: digitado ou pelo atalho "Confere" da linha. */
export type EntryMode = "digitado" | "confere";

/** Justificativa da conferência e motivo da liberação: mínimo (o banco confere o mesmo). */
export const SETTLEMENT_REASON_MIN = 10;

/**
 * Por que o usuário atual não faz a etapa atual (null: pode fazer):
 *  - vendedor_da_carga: é (ou foi, durante a carga) o vendedor dela — vale para administradores;
 *  - sem_permissao: falta a permissão da etapa (registrar, fechar);
 *  - sem_permissao_liberar: há divergência e falta "aprovar divergência";
 *  - declarante: enviou a prestação de contas, não confirma a conferência;
 *  - quem_confirmou: carga anterior sem vínculo confiável — quem confirmou não finaliza.
 */
export type SettlementBlock =
  | "vendedor_da_carga"
  | "sem_permissao"
  | "sem_permissao_liberar"
  | "declarante"
  | "quem_confirmou"
  | "prestacao_desatualizada"
  | "revisor_da_prestacao";

export type SettlementNextAction = {
  stage: SettlementStage;
  /** o que falta, em uma frase */
  message: string;
  block: SettlementBlock | null;
  /** quem pode fazer, quando não é o usuário atual */
  whoCan: string | null;
};
export type SettlementStatus = keyof typeof SETTLEMENT_STATUS_LABEL;

/** Dinheiro/cheque: em mãos do vendedor (entrega no acerto); PIX/cartão/transferência: direto na empresa. */
export const PAYMENT_HANDLING_LABEL = {
  em_maos: "Em mãos do vendedor (entregar no acerto)",
  direto: "Direto na empresa (conferir)",
} as const;
export type PaymentHandling = keyof typeof PAYMENT_HANDLING_LABEL;
const DIRECT_KINDS = ["pix", "cartao_credito", "cartao_debito", "transferencia", "boleto", "prazo"];
/** Mesma regra do banco (app.payment_handling). */
export function paymentHandling(kind: string): PaymentHandling {
  return DIRECT_KINDS.includes(kind) ? "direto" : "em_maos";
}

export const settlementCountSchema = z.object({
  productId: z.uuid(),
  /** vazio: contagem do produto inteiro (o sistema compõe os lotes) */
  lotId: optionalUuid,
  counted: qtyOrZero,
});
export type SettlementCountInput = z.output<typeof settlementCountSchema>;

/**
 * Prestação de contas do vendedor (ou do escritório em nome de vendedor sem usuário): a
 * contagem declarada e o dinheiro/cheque em mãos. Editável até o envio; depois, preservada.
 */
export const settlementDeclarationSchema = z.object({
  /** versão do acerto em rascunho (null: ainda não existe) */
  version: z.number().int().min(1).nullable(),
  notes: optionalText(1000),
  counts: z.array(settlementCountSchema).max(2000),
  /** só dinheiro/cheque (PIX, cartão e transferência a empresa confere) */
  payments: z.array(z.object({ paymentMethodId: z.uuid(), declared: moneyOrZero })).max(100),
});
export type SettlementDeclarationInput = z.input<typeof settlementDeclarationSchema>;

/**
 * Envio da prestação de contas. `basis`: o resumo da carga que estava na tela (Settlement.basis);
 * se a carga mudou depois, o envio é recusado (PRESTACAO_DESATUALIZADA). Opcional para versões
 * anteriores do aplicativo; obrigatório no reenvio depois de uma revisão.
 */
export const settlementSendSchema = z.object({
  ...versionSchema.shape,
  basis: z
    .string()
    .regex(/^[0-9a-f]{64}$/, "Resumo da carga inválido")
    .optional(),
});
export type SettlementSendInput = z.input<typeof settlementSendSchema>;

/**
 * Revisão da prestação de contas desatualizada: guarda a versão enviada e reabre a prestação. O
 * escritório (com a permissão própria) precisa justificar.
 */
export const settlementReviseSchema = z.object({
  ...versionSchema.shape,
  reason: optionalText(1000),
});
export type SettlementReviseInput = z.input<typeof settlementReviseSchema>;

const nullableQty = z
  .union([decimalSchema(6), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v === "" || v === undefined ? null : v));
const nullableMoney = z
  .union([decimalSchema(2, { max: 999_999_999_999 }), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v === "" || v === undefined ? null : v));
const entryModeSchema = z.enum(["digitado", "confere"]).default("digitado");
const reasonText = (label: string) =>
  optionalText(500).refine((v) => v === null || v.length >= SETTLEMENT_REASON_MIN, {
    message: `${label}: escreva pelo menos ${SETTLEMENT_REASON_MIN} caracteres`,
  });

/**
 * Etapa 1 — retorno informado pelo escritório: a contagem física por lote e o valor conferido por
 * forma de pagamento. Só o que o operador informou é gravado (null apaga a linha); nada é
 * preenchido com o esperado. Linhas que não vierem ficam como estão.
 */
export const settlementReturnSchema = z.object({
  /** versão do acerto (null: ainda não existe) */
  version: z.number().int().min(1).nullable(),
  counts: z
    .array(
      z.object({
        productId: z.uuid(),
        /** null: produto sem lote */
        lotId: optionalUuid,
        counted: nullableQty,
        mode: entryModeSchema,
      }),
    )
    .max(2000),
  payments: z
    .array(z.object({ paymentMethodId: z.uuid(), delivered: nullableMoney, mode: entryModeSchema }))
    .max(100),
});
export type SettlementReturnInput = z.input<typeof settlementReturnSchema>;

/** Etapa 2 — confirmação da conferência: o que foi revisado (fingerprint) e a justificativa. */
export const settlementConfirmSchema = z.object({
  ...versionSchema.shape,
  fingerprint: z.string().min(16, "Recarregue o acerto antes de confirmar").max(128),
  /** obrigatória (mínimo SETTLEMENT_REASON_MIN) quando há diferença */
  checkReason: reasonText("Justificativa"),
});
export type SettlementConfirmInput = z.input<typeof settlementConfirmSchema>;

export const settlementCloseSchema = z.object({
  ...versionSchema.shape,
  /** o que quem fecha conferiu (Settlement.fingerprint): mudou (venda, carregamento, estorno)? recusa */
  fingerprint: z.string().min(1, "Recarregue o acerto antes de fechar").max(128),
  /** diferença de mercadoria, de dinheiro ou declarado × conferido: exige aprovar_divergencia */
  releaseDivergence: z.boolean().default(false),
  divergenceReason: reasonText("Motivo da liberação"),
});

/** Livro da carga por lote (carregado e vendido, líquidos de estornos). */
export type LedgerLot = {
  lotId: string | null;
  lotCode: string | null;
  lotExpiresOn: string | null;
  loaded: string;
  sold: string;
};

/** Como a contagem foi feita: o produto inteiro ou lote a lote. */
export type CountMode = "produto" | "lote";

export type AllocatedLot = LedgerLot & {
  /** deveria estar com o vendedor: carregado − vendido */
  remaining: string;
  /** declarado pelo vendedor neste lote (quando declarou por lote) */
  declared: string | null;
  /** conferido pelo escritório neste lote (quando conferiu por lote) */
  counted: string | null;
  /** quem informou o conferido deste lote e como (null: ainda não informado) */
  countedByEmail: string | null;
  countedMode: EntryMode | null;
  /** devolvido (composto pelo sistema a partir da contagem conferida) */
  returned: string | null;
  /** falta (> 0) ou sobra (< 0) */
  difference: string | null;
};

export type SettlementProduct = {
  productId: string;
  productCode: string;
  productName: string;
  baseUnitCode: string;
  controlsLot: boolean;
  /** custo médio atual (prévia do valor da falta/sobra; no fechamento o banco grava o dele) */
  unitCost: string | null;
  loaded: string;
  sold: string;
  remaining: string;
  /** como o vendedor declarou e quanto (total do produto) */
  declaredMode: CountMode | null;
  declared: string | null;
  /** como o escritório conferiu e quanto (total do produto) */
  countMode: CountMode | null;
  counted: string | null;
  /** conferido − declarado (depois da conferência) */
  checkDifference: string | null;
  /** o conferido difere do declarado (no total ou, se os dois contaram por lote, num lote) */
  checkDivergence: boolean;
  returned: string | null;
  difference: string | null;
  lots: AllocatedLot[];
  /** o que impede o fechamento neste produto (ex.: informar por lote) */
  issue: string | null;
  needsLotCount: boolean;
};

/** Livro da carga de um produto (entrada da regra de devolução). */
export type LoadProductLedger = { productCode: string; controlsLot: boolean; lots: LedgerLot[] };
/**
 * Uma linha conferida: de um lote (lotId) ou do produto sem lote (lotId null), com quem informou
 * e como.
 */
export type CountEntry = {
  lotId: string | null;
  counted: string;
  byEmail?: string | null;
  mode?: EntryMode | null;
};
/** Devolução composta por lote a partir da contagem. */
export type Allocation = {
  lots: AllocatedLot[];
  countMode: CountMode | null;
  issue: string | null;
  needsLotCount: boolean;
};

/**
 * Devolução lote a lote a partir da contagem física conferida (acerto simplificado):
 *  - cada lote da carga vale o que foi contado NELE (produto sem lote: a linha do produto);
 *  - lote sem contagem fica pendente (devolvido e diferença em branco): nada vira o saldo teórico;
 *  - contagem de lote que não veio na carga, ou do produto inteiro quando ele tem lote, é pendência.
 * Sempre: carregado = vendido + devolvido + diferença, por lote.
 */
export function composeLotReturns(product: LoadProductLedger, counts: CountEntry[]): Allocation {
  const known = new Set(product.lots.map((l) => l.lotId));
  const byLot = new Map(counts.filter((c) => known.has(c.lotId)).map((c) => [c.lotId, c]));
  const lots: AllocatedLot[] = product.lots.map((l) => {
    const remaining = fromScaled(toScaled(l.loaded) - toScaled(l.sold));
    const c = byLot.get(l.lotId);
    return {
      ...l,
      remaining,
      declared: null,
      counted: c ? c.counted : null,
      countedByEmail: c?.byEmail ?? null,
      countedMode: c?.mode ?? null,
      returned: c ? c.counted : null,
      difference: c ? fromScaled(toScaled(remaining) - toScaled(c.counted)) : null,
    };
  });
  const foreign = counts.filter((c) => !known.has(c.lotId));
  let issue: string | null = null;
  if (foreign.some((c) => c.lotId === null)) {
    issue = `${product.productCode}: conte lote a lote (a contagem do produto inteiro não vale).`;
  } else if (foreign.length > 0) {
    issue = `${product.productCode}: há contagem de lote que não veio nesta carga: retire-a.`;
  }
  return {
    lots,
    countMode:
      byLot.size === 0 ? null : product.lots.some((l) => l.lotId !== null) ? "lote" : "produto",
    issue,
    needsLotCount: false,
  };
}

/** Totais de um produto a partir dos lotes compostos. */
export function summarizeProduct(lots: AllocatedLot[]) {
  const sum = (f: (l: AllocatedLot) => string | null) =>
    lots.some((l) => f(l) === null)
      ? null
      : fromScaled(lots.reduce((acc, l) => acc + toScaled(f(l) ?? "0"), 0n));
  return {
    loaded: sum((l) => l.loaded) ?? "0",
    sold: sum((l) => l.sold) ?? "0",
    remaining: sum((l) => l.remaining) ?? "0",
    returned: sum((l) => l.returned),
    difference: sum((l) => l.difference),
  };
}

export type SettlementPayment = {
  paymentMethodId: string;
  paymentMethodName: string;
  kind: string;
  handling: PaymentHandling;
  expected: string;
  /** declarado pelo vendedor (só dinheiro/cheque em mãos) */
  declared: string | null;
  /** conferido pelo escritório (null: ainda não conferido) */
  delivered: string | null;
  /** conferido − esperado */
  difference: string | null;
  /** conferido − declarado (dinheiro/cheque) */
  checkDifference: string | null;
  /** quem informou o conferido e como */
  deliveredByEmail: string | null;
  deliveredMode: EntryMode | null;
};

/** Acerto estornado: quem declarou, quem conferiu e o motivo ficam visíveis. */
export type SettlementHistory = {
  id: string;
  number: string;
  declaredAt: string | null;
  declaredByEmail: string | null;
  checkedAt: string | null;
  checkedByEmail: string | null;
  checkReason: string | null;
  checkDivergence: boolean;
  confirmedAt: string | null;
  confirmedByEmail: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
};

/** O que mudou na carga depois do envio da prestação de contas (datas gravadas pelo banco). */
export type SettlementDeclarationChange = {
  kind:
    | "venda"
    | "venda_cancelada"
    | "venda_estornada"
    | "carregamento"
    | "carregamento_estornado"
    | "recebimento"
    | "recebimento_estornado";
  number: string;
  at: string;
  amount: string | null;
};

export const SETTLEMENT_DECLARATION_CHANGE_LABEL: Record<
  SettlementDeclarationChange["kind"],
  string
> = {
  venda: "Venda",
  venda_cancelada: "Venda cancelada",
  venda_estornada: "Venda estornada",
  carregamento: "Carregamento",
  carregamento_estornado: "Carregamento estornado",
  recebimento: "Recebimento",
  recebimento_estornado: "Recebimento estornado",
};

/** Versão anterior da prestação de contas (guardada na revisão; não muda mais). */
export type SettlementDeclarationVersion = {
  id: string;
  round: number;
  declaredAt: string;
  declaredByName: string | null;
  /** a base gravada no envio, ou reconstituída pela data do envio (prestação anterior à revisão) */
  basisReference: "envio" | "reconstituida";
  notes: string | null;
  counts: {
    productId: string;
    productCode: string;
    productName: string;
    lotId: string | null;
    lotCode: string | null;
    counted: string;
  }[];
  payments: {
    paymentMethodId: string;
    paymentMethodName: string;
    handling: PaymentHandling;
    expected: string;
    declared: string | null;
  }[];
  reopenedAt: string;
  reopenedByName: string | null;
  reopenKind: "vendedor" | "escritorio";
  reopenReason: string | null;
  changes: SettlementDeclarationChange[];
};

/** Quem pode revisar a prestação desatualizada (para quem está vendo). */
export type SettlementRevision = {
  /** "vendedor": o vendedor da carga; "escritorio": exige permissão e justificativa */
  as: "vendedor" | "escritorio" | null;
  /** por que quem está vendo não pode revisar */
  blockedReason: string | null;
};

export type Settlement = {
  id: string | null;
  number: string | null;
  status: SettlementStatus | null;
  origin: SettlementOrigin | null;
  /** etapa atual e o que falta (para o usuário que pediu) */
  stage: SettlementStage;
  nextAction: SettlementNextAction;
  /** conferência confirmada e ainda válida (nada mudou desde a confirmação) */
  confirmed: boolean;
  /** foi confirmada, mas o acerto mudou depois (venda, estorno, carregamento): confirmar de novo */
  checkStale: boolean;
  /** linhas ainda sem valor conferido (produto/lote ou forma de pagamento) */
  gaps: string[];
  version: number | null;
  notes: string | null;
  registeredByEmail: string | null;
  registeredAt: string | null;
  /** envio da prestação de contas (depois dele o declarado não muda) */
  declaredAt: string | null;
  declaredByEmail: string | null;
  /** conferência do escritório: quem, quando e o motivo quando difere do declarado */
  checkedAt: string | null;
  checkedByEmail: string | null;
  checkReason: string | null;
  /** o conferido difere do declarado (mercadoria ou dinheiro), ou quem conferiu foi quem declarou */
  checkDivergence: boolean;
  /** conferido pela mesma pessoa que declarou (em nome do vendedor): conta como divergência */
  checkedByDeclarant: boolean;
  /** dinheiro/cheque: conferido − declarado (gravado no fechamento) */
  checkCashDifference: string | null;
  confirmedAt: string | null;
  confirmedByEmail: string | null;
  divergenceReleasedByEmail: string | null;
  divergenceReleasedAt: string | null;
  divergenceReason: string | null;
  goodsDifferenceValue: string | null;
  cashDifference: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  products: SettlementProduct[];
  payments: SettlementPayment[];
  /** há diferença de mercadoria ou de dinheiro (exige justificativa e liberação) */
  hasDivergence: boolean;
  /** pendências que impedem o fechamento */
  issues: string[];
  /** resumo do que está na tela (mercadoria por lote e dinheiro por forma) para o fechamento */
  fingerprint: string;
  /** acertos anteriores estornados desta carga (com a prestação de contas e a conferência) */
  history: SettlementHistory[];
  /** resumo atual do que move a carga (vai junto no envio: se mudar, o envio é recusado) */
  basis: string | null;
  /** a prestação enviada não confere mais com a carga: conferência e finalização bloqueadas */
  declarationStale: boolean;
  /** o que mudou na carga depois do envio (quando desatualizada) */
  declarationChanges: SettlementDeclarationChange[];
  /** versões anteriores da prestação de contas (revisões) */
  declarationVersions: SettlementDeclarationVersion[];
  /** revisão assumida pelo escritório: quem e por quê (não confere nem finaliza este acerto) */
  officeReview: { byName: string | null; reason: string } | null;
  /** para quem está vendo: pode revisar a prestação desatualizada? */
  revision: SettlementRevision | null;
};

export type LoadDetail = Load & {
  shipments: LoadShipment[];
  orders: LoadOrder[];
  settlement: Settlement;
  movements: StockMovement[];
};

/** Situação do acerto no relatório: quem declarou, quem conferiu e quem liberou. */
export type LoadReportSettlement = {
  status: Settlement["status"];
  number: Settlement["number"];
  confirmedAt: Settlement["confirmedAt"];
  declaredAt: Settlement["declaredAt"];
  declaredByEmail: Settlement["declaredByEmail"];
  checkedAt: Settlement["checkedAt"];
  checkedByEmail: Settlement["checkedByEmail"];
  checkReason: Settlement["checkReason"];
  checkDivergence: Settlement["checkDivergence"];
  divergenceReleasedByEmail: Settlement["divergenceReleasedByEmail"];
  divergenceReason: Settlement["divergenceReason"];
};

/** Relatório do acerto: as nove perguntas. */
export type LoadReport = {
  load: Load;
  /** 1. quanto levou; 2. quanto vendeu; 5. quanto voltou */
  products: {
    productCode: string;
    productName: string;
    baseUnitCode: string;
    loaded: string;
    sold: string;
    /** declarado pelo vendedor × conferido pelo escritório */
    declared: string | null;
    counted: string | null;
    returned: string | null;
    difference: string | null;
    lots: {
      lotCode: string | null;
      loaded: string;
      sold: string;
      returned: string | null;
      difference: string | null;
    }[];
  }[];
  /** 3. para quais clientes vendeu */
  customers: {
    customerName: string;
    orders: number;
    total: string;
    aVista: string;
    aPrazo: string;
  }[];
  /** 4. quanto recebeu e em qual forma */
  receipts: SettlementPayment[];
  aPrazoTotal: string;
  salesTotal: string;
  /** 6. deveria entregar; 7. entregou; 8/9. diferença */
  cashExpected: string;
  cashDelivered: string | null;
  cashDifference: string | null;
  goodsDifferenceValue: string | null;
  commission: string;
  settlement: LoadReportSettlement;
};
